import { useMemo } from "react";
import { fixedPriceItems, pvcRollingShutterSizes, profileHandlePrices } from "../data/constants";

export const useOrderCalculator = ({ cabinets, customerInfo, country }) => {
  const calculateTotal = (cab) => {
    if (country === "Shaker") {
      if (!cab.type || !cab.unitCost || !cab.qty) return 0;
      
      const cost = parseFloat(cab.unitCost) || 0;
      const qty = parseFloat(cab.qty) || 1;
      const totalCost = cost * qty;
      const discountPercent = parseFloat(cab.discount) || 0;
      const discountAmount = (totalCost * discountPercent) / 100;
      
      console.log("🔍 SHAKER CALCULATION:", {
        item: cab.type,
        unitCost: cost,
        quantity: qty,
        discount: discountPercent,
        totalBeforeDiscount: totalCost,
        discountAmount: discountAmount,
        finalTotal: totalCost - discountAmount
      });
      
      return Math.max(totalCost - discountAmount, 0);
    }

    // Check if it's Profile Handle
    if (cab.type === "Profile Handle") {
      const qty = parseFloat(cab.qty) || 1;
      const profileHandleInches = cab.profileHandleInches || "6";
      
      // Get the price from profileHandlePrices based on inches
      const pricePerInch = profileHandlePrices[profileHandleInches] || 0;
      
      // Multiply by 4 as requested
      const priceAfter4x = pricePerInch * 4;
      
      // Calculate total cost before discount
      const totalCostBeforeDiscount = priceAfter4x * qty;
      
      // Apply discount
      const discountPercent = parseFloat(cab.discount) || 0;
      const discountAmount = (totalCostBeforeDiscount * discountPercent) / 100;
      
      // Final total after discount (in rupees)
      const finalTotalRupees = Math.max(totalCostBeforeDiscount - discountAmount, 0);
      
      // Convert to dollars (assuming 1 USD = 89 INR)
      const finalTotalInDollars = finalTotalRupees / 89;
      
      console.log("🔧 PROFILE HANDLE CALCULATION:", {
        inches: profileHandleInches,
        pricePerInch: pricePerInch,
        after4xMultiplier: priceAfter4x,
        quantity: qty,
        totalBeforeDiscount: totalCostBeforeDiscount,
        discountApplied: discountAmount,
        finalTotalRupees: finalTotalRupees,
        finalTotalDollars: finalTotalInDollars
      });
      
      return finalTotalInDollars;
    }

    // Check if it's PVC Rolling Shutter
    if (cab.type === "PVC Rolling Shutter") {
      const unitCost = parseFloat(cab.unitCost) || 0;
      const qty = parseFloat(cab.qty) || 1;
      const totalCost = unitCost * qty;
      const discountPercent = parseFloat(cab.discount) || 0;
      const discountAmount = (totalCost * discountPercent) / 100;
      
      console.log("🏗️ PVC ROLLING SHUTTER CALCULATION:", {
        size: cab.pvcSize,
        color: cab.color,
        unitPrice: unitCost,
        quantity: qty,
        discount: discountPercent,
        totalBeforeDiscount: totalCost,
        discountAmount: discountAmount,
        finalTotal: totalCost - discountAmount
      });
      
      return Math.max(totalCost - discountAmount, 0);
    }

    // Check if it's a fixed price item
    const isFixedPriceItem = fixedPriceItems[cab.type];
    
    if (isFixedPriceItem) {
      // Fixed price item calculation
      const unitCost = fixedPriceItems[cab.type] || 0;
      const qty = parseFloat(cab.qty) || 1;
      const totalCost = unitCost * qty;
      const discountPercent = parseFloat(cab.discount) || 0;
      const discountAmount = (totalCost * discountPercent) / 100;
      
      console.log("💰 FIXED PRICE CALCULATION:", {
        item: cab.type,
        unitPrice: unitCost,
        quantity: qty,
        discount: discountPercent,
        totalBeforeDiscount: totalCost,
        discountAmount: discountAmount,
        finalTotal: totalCost - discountAmount
      });
      
      return Math.max(totalCost - discountAmount, 0);
    }

    // Regular Custom Cabinet Calculation
    const height = parseFloat(cab.height) || 0;
    const width = parseFloat(cab.width) || 0;
    const unitCost = parseFloat(cab.unitCost) || 0;
    const qty = parseFloat(cab.qty) || 1;
    const drawerSlides = parseFloat(cab.drawerSlides) || 0;
    const hinges = parseFloat(cab.hinges) || 0;
    
    // Check if all required fields are filled
    const hasRequiredFields = cab.type && height > 0 && width > 0 && cab.color && unitCost > 0;
    
    if (!hasRequiredFields) {
      return 0;
    }

    // Convert inches to feet
    const heightInFeet = height / 12;
    const widthInFeet = width / 12;
    
    // Calculate square feet
    const sqft = heightInFeet * widthInFeet;
    
    // Calculate shutter price using the color's specific price
    const shutterCost = sqft * unitCost;
    
    // Carcass price (fixed ₹4860 per cabinet)
    const carcassPrice = 4860;
    
    // Drawer Slides charges (₹250 per unit) - ONLY FOR CUSTOM
    const drawerSlidesCost = drawerSlides * 250;
    
    // Hinges charges (₹85 per unit) - ONLY FOR CUSTOM
    const hingesCost = hinges * 85;
    
    // Calculate total cost before discount
    const totalCostBeforeDiscount = (shutterCost + carcassPrice + drawerSlidesCost + hingesCost) * 4 * qty;
    
    // Apply discount
    const discountPercent = parseFloat(cab.discount) || 0;
    const discountAmount = (totalCostBeforeDiscount * discountPercent) / 100;
    
    // Final total after discount
    const finalTotal = Math.max(totalCostBeforeDiscount - discountAmount, 0);
    
    // Convert to dollars (assuming 1 USD = 89 INR)
    const finalTotalInDollars = finalTotal / 89;
    
    console.log("🧮 CUSTOM CALCULATION STEPS:", {
      heightInFeet: heightInFeet,
      widthInFeet: widthInFeet,
      sqft: sqft,
      colorPricePerSqft: unitCost,
      shutterCost: shutterCost,
      carcassPrice: carcassPrice,
      drawerSlides: drawerSlides,
      drawerSlidesCost: drawerSlidesCost,
      hinges: hinges,
      hingesCost: hingesCost,
      baseCost: shutterCost + carcassPrice + drawerSlidesCost + hingesCost,
      after4xMultiplier: (shutterCost + carcassPrice + drawerSlidesCost + hingesCost) * 4,
      quantityMultiplier: qty,
      totalBeforeDiscount: totalCostBeforeDiscount,
      discountApplied: discountAmount,
      finalTotalRupees: finalTotal,
      finalTotalDollars: finalTotalInDollars
    });
    
    return finalTotalInDollars;
  };

  const calculateSqft = (cab) => {
    if (country === "Shaker") return 0;
    
    // Fixed price items, PVC Rolling Shutter, and Profile Handle don't have sqft
    if (fixedPriceItems[cab.type] || cab.type === "PVC Rolling Shutter" || cab.type === "Profile Handle") {
      return 0;
    }
    
    const height = parseFloat(cab.height) || 0;
    const width = parseFloat(cab.width) || 0;
    
    if (!height || !width) return 0;
    
    const heightInFeet = height / 12;
    const widthInFeet = width / 12;
    return heightInFeet * widthInFeet;
  };

  // Calculate subtotal (before tax and overall discount)
  const subtotal = useMemo(() => {
    const total = cabinets.reduce((sum, cab) => sum + calculateTotal(cab), 0);
    console.log("📊 SUBTOTAL CALCULATION:", {
      cabinetsCount: cabinets.length,
      subtotal: total,
      cabinetDetails: cabinets.map(cab => ({
        type: cab.type,
        total: calculateTotal(cab)
      }))
    });
    return total;
  }, [cabinets, country]);

  // Calculate grand total (after overall discount but before tax)
  const grandTotal = useMemo(() => {
    const overallDiscount = (parseFloat(customerInfo.discount) || 0) / 100;
    const discountAmount = subtotal * overallDiscount;
    const totalAfterDiscount = subtotal - discountAmount;
    
    console.log("💰 GRAND TOTAL CALCULATION:", {
      subtotal: subtotal,
      overallDiscount: overallDiscount * 100 + "%",
      discountAmount: discountAmount,
      totalAfterDiscount: totalAfterDiscount
    });
    
    return totalAfterDiscount;
  }, [subtotal, customerInfo.discount]);

  // Calculate sales tax (6.625%)
  const salesTax = useMemo(() => {
    const taxRate = 6.625;
    const taxAmount = (grandTotal * taxRate) / 100;
    
    console.log("🧾 SALES TAX CALCULATION:", {
      grandTotal: grandTotal,
      taxRate: taxRate + "%",
      taxAmount: taxAmount
    });
    
    return taxAmount;
  }, [grandTotal]);

  // Calculate final total (including tax)
  const finalTotal = useMemo(() => {
    const total = grandTotal + salesTax;
    
    console.log("🎯 FINAL TOTAL CALCULATION:", {
      grandTotal: grandTotal,
      salesTax: salesTax,
      finalTotal: total
    });
    
    return total;
  }, [grandTotal, salesTax]);

  return { 
    grandTotal, 
    subtotal,
    salesTax,
    finalTotal,
    calculateTotal, 
    calculateSqft 
  };
};

export default useOrderCalculator;